import React, { useCallback, useEffect, useReducer, useState } from "react";
import { fetchAppConfig, putAdminAppConfig } from "./appConfigApi";
import {
  MARKETPLACE_REWARD_TRIGGERS,
  normalizeMarketplaceSettings,
  resolvePhaseCompleteEligibleRoleTypeIds,
} from "./marketplaceRewardTriggers";
import { loadUserMatrixSettings } from "./userMatrixStorage";
import MarketplaceCoinIcon from "./MarketplaceCoinIcon";

function PhaseCompleteRoleChecks({ triggerId, roleTypes, selectedIds, onToggle }) {
  const selected = new Set(selectedIds);
  if (roleTypes.length === 0) {
    return <span className="email-triggers-muted">No user roles defined in the User Matrix.</span>;
  }
  return (
    <ul className="marketplace-rewards-role-checks">
      {roleTypes.map((role) => (
        <li key={role.id}>
          <label className="email-triggers-check-label">
            <input
              type="checkbox"
              checked={selected.has(role.id)}
              onChange={(e) => onToggle(role.id, e.target.checked)}
            />
            <span>{role.name}</span>
          </label>
        </li>
      ))}
    </ul>
  );
}

export default function MarketplaceSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [settings, setSettings] = useState(() => normalizeMarketplaceSettings(null));
  const [, bumpMatrixRev] = useReducer((x) => x + 1, 0);

  const matrix = loadUserMatrixSettings();
  const roleTypes = matrix.userTypes || [];

  useEffect(() => {
    const on = () => bumpMatrixRev();
    window.addEventListener("pb-user-matrix-settings-changed", on);
    return () => window.removeEventListener("pb-user-matrix-settings-changed", on);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const cfg = await fetchAppConfig();
      setSettings(normalizeMarketplaceSettings(cfg?.marketplaceSettings));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not load marketplace settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateTrigger = useCallback((triggerId, patch) => {
    setSettings((prev) => {
      const cur = prev.rewards[triggerId] || { enabled: true, amount: 0 };
      return {
        ...prev,
        rewards: {
          ...prev.rewards,
          [triggerId]: { ...cur, ...patch },
        },
      };
    });
    setMessage("");
  }, []);

  const toggleEligibleRole = useCallback(
    (triggerId, roleTypeId, checked) => {
      const cur = settings.rewards[triggerId] || { enabled: true, amount: 0 };
      const currentIds = resolvePhaseCompleteEligibleRoleTypeIds(triggerId, matrix, settings);
      const set = new Set(currentIds);
      if (checked) set.add(roleTypeId);
      else set.delete(roleTypeId);
      updateTrigger(triggerId, { eligibleRoleTypeIds: [...set].sort() });
    },
    [settings, matrix, updateTrigger]
  );

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const normalized = normalizeMarketplaceSettings(settings);
      const cfg = await putAdminAppConfig({ marketplaceSettings: normalized });
      setSettings(normalizeMarketplaceSettings(cfg?.marketplaceSettings));
      setMessage("Marketplace reward settings saved.");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not save marketplace settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="plugins-card plugins-card-wide marketplace-settings-panel">
        <p className="plugins-card-desc">Loading marketplace settings…</p>
      </section>
    );
  }

  const phaseCompleteTriggers = MARKETPLACE_REWARD_TRIGGERS.filter((d) => d.phaseComplete);
  const otherTriggers = MARKETPLACE_REWARD_TRIGGERS.filter((d) => !d.phaseComplete);

  return (
    <section className="plugins-card plugins-card-wide marketplace-settings-panel">
      <h3 className="plugins-card-name" style={{ marginTop: 0 }}>
        Marketplace rewards
      </h3>
      <p className="plugins-card-desc">
        Configure which actions earn V-coins and how many coins each action awards. For phase-complete
        rewards, also choose which User Matrix roles are eligible. Disabled triggers or zero amounts do
        not pay out.
      </p>

      <form onSubmit={onSave}>
        <div className="marketplace-rewards-table-wrap">
          <table className="marketplace-rewards-table">
            <thead>
              <tr>
                <th scope="col">Trigger</th>
                <th scope="col">Enabled</th>
                <th scope="col">V-coins</th>
              </tr>
            </thead>
            <tbody>
              {otherTriggers.map((def) => {
                const tr = settings.rewards[def.id] || { enabled: false, amount: 0 };
                return (
                  <tr key={def.id}>
                    <td>
                      <div className="marketplace-rewards-trigger-label">{def.label}</div>
                      <div className="marketplace-rewards-trigger-desc">{def.description}</div>
                      <code className="project-change-log-field">{def.id}</code>
                    </td>
                    <td className="marketplace-rewards-toggle-cell">
                      <label className="plugins-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(tr.enabled)}
                          onChange={(ev) => updateTrigger(def.id, { enabled: ev.target.checked })}
                        />
                        <span>{tr.enabled ? "On" : "Off"}</span>
                      </label>
                    </td>
                    <td className="marketplace-rewards-amount-cell">
                      <div className="marketplace-rewards-amount-input">
                        <MarketplaceCoinIcon size={18} />
                        <input
                          type="number"
                          min={0}
                          max={100000}
                          step={1}
                          value={tr.amount}
                          disabled={!tr.enabled}
                          onChange={(ev) =>
                            updateTrigger(def.id, {
                              amount: Math.max(0, Math.trunc(Number(ev.target.value) || 0)),
                            })
                          }
                          aria-label={`V-coins for ${def.label}`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <h4 className="marketplace-rewards-phase-heading">Phase-complete rewards</h4>
        <p className="plugins-card-desc marketplace-rewards-phase-lead">
          Eligible roles receive V-coins when that phase is marked complete on a project. Project
          assignees (sales rep, drafter, laborers, etc.) are also included when relevant.
        </p>

        <div className="marketplace-rewards-table-wrap">
          <table className="marketplace-rewards-table marketplace-rewards-table--phase">
            <thead>
              <tr>
                <th scope="col">Phase</th>
                <th scope="col">Enabled</th>
                <th scope="col">V-coins</th>
                <th scope="col">Eligible roles</th>
              </tr>
            </thead>
            <tbody>
              {phaseCompleteTriggers.map((def) => {
                const tr = settings.rewards[def.id] || { enabled: false, amount: 0 };
                const eligibleRoleTypeIds = resolvePhaseCompleteEligibleRoleTypeIds(
                  def.id,
                  matrix,
                  settings
                );
                return (
                  <tr key={def.id}>
                    <td>
                      <div className="marketplace-rewards-trigger-label">{def.label}</div>
                      <div className="marketplace-rewards-trigger-desc">{def.description}</div>
                      <code className="project-change-log-field">{def.id}</code>
                    </td>
                    <td className="marketplace-rewards-toggle-cell">
                      <label className="plugins-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(tr.enabled)}
                          onChange={(ev) => updateTrigger(def.id, { enabled: ev.target.checked })}
                        />
                        <span>{tr.enabled ? "On" : "Off"}</span>
                      </label>
                    </td>
                    <td className="marketplace-rewards-amount-cell">
                      <div className="marketplace-rewards-amount-input">
                        <MarketplaceCoinIcon size={18} />
                        <input
                          type="number"
                          min={0}
                          max={100000}
                          step={1}
                          value={tr.amount}
                          disabled={!tr.enabled}
                          onChange={(ev) =>
                            updateTrigger(def.id, {
                              amount: Math.max(0, Math.trunc(Number(ev.target.value) || 0)),
                            })
                          }
                          aria-label={`V-coins for ${def.label}`}
                        />
                      </div>
                    </td>
                    <td className="marketplace-rewards-roles-cell">
                      <PhaseCompleteRoleChecks
                        triggerId={def.id}
                        roleTypes={roleTypes}
                        selectedIds={eligibleRoleTypeIds}
                        onToggle={(roleId, checked) => toggleEligibleRole(def.id, roleId, checked)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {error ? (
          <p className="email-triggers-muted" role="alert" style={{ color: "#b91c1c" }}>
            {error}
          </p>
        ) : null}
        {message ? <p className="email-triggers-muted">{message}</p> : null}

        <div className="marketplace-settings-actions">
          <button type="submit" className="project-comments-submit-btn" disabled={saving}>
            {saving ? "Saving…" : "Save rewards"}
          </button>
        </div>
      </form>
    </section>
  );
}
